
import { Job, UserProfile, LocalRequirement } from "../types";

export const analyzeJobMatch = async (job: Job, worker: UserProfile) => {
  try {
    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `
        Analyze the fit between this tree care job and this professional.
        
        Job Details:
        Title: ${job.title}
        Description: ${job.description}
        Required Skills: ${job.requiredSkills.join(', ')}
        Required Certs: ${job.requiredCerts.join(', ')}
        
        Professional Profile:
        Name: ${worker.name}
        Skills: ${worker.skills.join(', ')}
        Certifications: ${worker.certifications.join(', ')}
        Bio: ${worker.bio}
        
        Provide a match score (0-100) and a brief summary of why they are or aren't a good fit.
      `,
        model: 'gemini-3.6-flash'
      })
    });

    const data = await response.json();
    const result = JSON.parse(data.text || '{"score": 0, "reasoning": "Failed to analyze."}');
    return result;
  } catch (error) {
    console.error("Gemini Analysis Error:", error);
    return { score: 50, reasoning: "Manual review required due to technical error." };
  }
};

export const analyzeOperations = async (prompt: string, context: { jobs: Job[], workers: UserProfile[], globalRequirements: LocalRequirement[] }) => {
  try {
    const response = await fetch('/api/ai/chat', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `
        You are the ArborLink AI Operations Assistant. Your goal is to optimize tree care operations and safety in the Columbus, GA and Phenix City, AL area.
        
        Current System State:
        - Jobs: ${JSON.stringify(context.jobs)}
        - Professionals: ${JSON.stringify(context.workers)}
        - Global Compliance: ${JSON.stringify(context.globalRequirements)}

        User Request: ${prompt}

        Special Instructions for Equipment/Machinery Queries:
        1. If asked about equipment or "what tools are needed", provide a technical breakdown based on arborist standards (ANSI Z133).
        2. Distinguish between personal gear (climbing kits, chainsaws), specialized rigging (GRCS, Port-a-wrap), and heavy machinery (Bucket trucks, Cranes, Grapple Saws, Chipper/Dump combos).
        3. For complex removals (over houses/structures), emphasize safety and suggest specific machine types (e.g., "A 60-ton crane with a grapple saw" or "a 90ft Spider Lift").
        4. USE GOOGLE SEARCH to find actual local equipment rental services in the Columbus, GA area (e.g., Sunbelt Rentals, United Rentals, or local specialized arbor dealers). Provide links or contact info if possible.
        
        General Operational Rules:
        1. Match workers based on SkillTypes and ISA Certifications.
        2. Flag regional compliance issues (GA/AL laws).
        3. Be technical, safety-oriented, and concise.
      `,
        model: 'gemini-3.6-flash'
      })
    });

    const data = await response.json();
    return data.text || "I couldn't generate an operational analysis at this time.";
  } catch (error) {
    console.error("AI Operations Error:", error);
    return "Operations assistant is currently offline. Please try again in a moment.";
  }
};
